import type { Observation, SchoolTeamSnapshot } from "@/config/teamsSample";
import type { TeamsCoachingSeed, TeamsObservationFocus } from "@/types/mentorNavigation";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function observationToFocus(observation: Observation): TeamsObservationFocus {
  return {
    id: observation.id,
    body: observation.body,
    tags: observation.tags.map((t) => ({ label: t.label, tone: t.tone })),
  };
}

export function snapshotToTeamsCoachingSeed(
  snapshot: SchoolTeamSnapshot,
  options?: { focusObservation?: Observation }
): TeamsCoachingSeed {
  const sortedFocus = [...snapshot.focusAreas].sort((a, b) => b.percent - a.percent);
  return {
    schoolName: snapshot.schoolName,
    semesterLabel: snapshot.semesterLabel,
    helperCount: snapshot.helperCount,
    focusAreas: sortedFocus.map((f) => ({ label: f.label, percent: f.percent })),
    firstObservationBody: snapshot.observations[0]?.body,
    focusObservation: options?.focusObservation
      ? observationToFocus(options.focusObservation)
      : undefined,
  };
}

function tagsHtml(tags: TeamsObservationFocus["tags"]): string {
  if (tags.length === 0) return "";
  return tags.map((t) => `<em>${escapeHtml(t.label)}</em>`).join(" · ");
}

/** Rich HTML for the first assistant message when arriving from My Team → Think it through. */
export function teamsCoachingSeedAssistantHtml(seed: TeamsCoachingSeed): string {
  const ctxLine = `<p>You opened this space from <strong>My Team</strong> for <strong>${escapeHtml(seed.schoolName)}</strong> (${escapeHtml(seed.semesterLabel)} · ${seed.helperCount} ${seed.helperCount === 1 ? "helper" : "helpers"}).</p>`;

  if (seed.focusObservation) {
    const tagLine = tagsHtml(seed.focusObservation.tags);
    return `
${ctxLine}
<p>You picked one observation to think through:</p>
<blockquote><em>&ldquo;${escapeHtml(seed.focusObservation.body)}&rdquo;</em>${tagLine ? `<br /><small>${tagLine}</small>` : ""}</blockquote>
<p>I&rsquo;m here as HeyBodhi to help you sit with what this might mean and how you might respond. Where do you want to start &mdash; what you&rsquo;re seeing on the ground, what you&rsquo;d like to be true, or what you&rsquo;re unsure about?</p>
`.trim();
  }

  const topTwo = seed.focusAreas.slice(0, 2);
  const focusLine =
    topTwo.length >= 2
      ? `Strongest signals so far include &ldquo;${escapeHtml(topTwo[0]!.label)}&rdquo; (${topTwo[0]!.percent}%) and &ldquo;${escapeHtml(topTwo[1]!.label)}&rdquo; (${topTwo[1]!.percent}%).`
      : topTwo.length === 1
        ? `Strongest signal so far is &ldquo;${escapeHtml(topTwo[0]!.label)}&rdquo; (${topTwo[0]!.percent}%).`
        : "";

  const obsPara = seed.firstObservationBody
    ? `<p><strong>Worth noticing:</strong> ${escapeHtml(seed.firstObservationBody)}</p>`
    : "";

  return `
${ctxLine}
${focusLine ? `<p><strong>What staff are reaching for:</strong> ${focusLine}</p>` : ""}
${obsPara}
<p>I&rsquo;m here as HeyBodhi to help you think through what this might mean for your leadership and how you might respond. What&rsquo;s on your mind?</p>
`.trim();
}
